import {controller, target, targets, attr} from '@github/catalyst'
import {getPlatform} from '../platform-toggle'
import safeStorage from '@github-ui/safe-storage'
import {trackView} from '../hydro-tracking'

const {getItem, setItem} = safeStorage('localStorage')

// for persisting which tab was last selected in localstorage
const DefaultTabKey = 'code-button-default-tab'

@controller('get-repo')
export class GetRepoElement extends HTMLElement {
  @attr forceCodespaceTabDefault = false

  @target declare modal: HTMLElement
  @target declare codespaceForm: HTMLFormElement
  @target declare codespaceLoadingMenu: HTMLElement | undefined
  @target declare codespaceList: HTMLElement | undefined
  @target declare codespaceSelector: HTMLElement
  @target declare openOrCreateInCodespace: HTMLElement
  @target declare vscodePoller: HTMLElement
  @targets declare platforms: HTMLElement[]
  @target declare copilotTip: HTMLElement
  private shouldRefreshList = false
  private hasForcedCodespaceTabDefault = false

  showDownloadMessage() {
    const app = this.findPlatform()
    if (!app) return

    this.showPlatform(app)
  }

  showCodespaces() {
    const app = this.findPlatform()
    if (!app) return

    this.showPlatform(app)
    this.loadAndUpdateContent()
  }

  showCodespaceSelector() {
    const app = this.findPlatform()
    if (!app) return

    this.showPlatform(app)
    if (this.codespaceSelector) this.codespaceSelector.hidden = false
  }

  showOpenOrCreateInCodespace() {
    if (this.openOrCreateInCodespace) this.openOrCreateInCodespace.hidden = false
  }

  removeOpenOrCreateInCodespace() {
    if (this.openOrCreateInCodespace) this.openOrCreateInCodespace.remove()
  }

  refreshList() {
    if (this.shouldRefreshList) {
      this.shouldRefreshList = false
      this.loadAndUpdateContent()
    }
  }

  trackDelete() {
    this.shouldRefreshList = true
  }

  hideSpinner() {
    if (this.codespaceLoadingMenu) this.codespaceLoadingMenu.hidden = true
    if (this.codespaceList) this.codespaceList.hidden = false
  }

  showSpinner() {
    if (this.codespaceLoadingMenu) this.codespaceLoadingMenu.hidden = false
    if (this.codespaceList) this.codespaceList.hidden = true
  }

  /* eslint-disable-next-line wc/no-method-prefixed-with-on */
  onDetailsToggle(e: Event) {
    this.modal.hidden = false

    for (const platform of this.platforms) {
      platform.hidden = true
    }
    const detailsEl = e.target as HTMLDetailsElement

    if (detailsEl && detailsEl.open) {
      if (!this.hasForcedCodespaceTabDefault && this.forceCodespaceTabDefault) {
        this.hasForcedCodespaceTabDefault = true
        this.selectDefaultTab(true)
      } else {
        this.selectDefaultTab(false)
      }

      const copilotEl = this.copilotTip
      if (copilotEl) {
        trackView(copilotEl)
      }
    }
  }

  /* eslint-disable-next-line wc/no-method-prefixed-with-on */
  onDetailsKeydown(e: KeyboardEvent) {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (e.key === 'Escape') {
      this.modal.hidden = true

      const element = e.target as HTMLElement
      element?.closest('details')?.removeAttribute('open')
    }
  }

  showPlatform(app: string) {
    this.modal.hidden = true

    for (const platform of this.platforms) {
      platform.hidden = platform.getAttribute('data-platform') !== app
    }
  }

  findPlatform() {
    return getPlatform()
  }

  refreshOnError() {
    // If we hit an error loading the codespaces list, reload the page.
    window.location.reload()
  }

  pollForVscode(event: CustomEvent) {
    this.showPlatform('vscode')

    const pollingUrl = (event.currentTarget as HTMLElement).getAttribute('data-src')
    if (pollingUrl) {
      try {
        const parsedUrl = new URL(pollingUrl, window.location.origin)
        const validProtocols = ['http:', 'https:', 'vscode:', 'vscode-insiders:']
        if (validProtocols.includes(parsedUrl.protocol)) {
          this.vscodePoller.setAttribute('src', parsedUrl.href)
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log('Error polling for VsCode:', error)
      }
    }
  }

  backToCodespacesFromVscodePolling() {
    this.loadAndUpdateContent()
    this.showPlatform('codespaces')
  }

  localTabSelected() {
    setItem(DefaultTabKey, 'local')
  }

  cloudTabSelected() {
    setItem(DefaultTabKey, 'cloud')
    if (this.codespaceList?.id !== 'lazyLoadedCodespacesList') return

    this.loadAndUpdateContent()
  }

  // The Copilot tab is visible when the `copilot_workspace` feature flag is enabled
  copilotTabSelected() {
    setItem(DefaultTabKey, 'copilot')
  }

  // When opening the code menu, select the tab which was most recently used.
  selectDefaultTab(forceCodespaceTabDefault: boolean) {
    const defaultTab = forceCodespaceTabDefault ? 'cloud' : getItem(DefaultTabKey)
    if (!defaultTab) return
    const button = this.querySelector(`button[data-tab="${defaultTab}"`) as HTMLButtonElement
    if (button) {
      button.click()
    }
  }

  private loadAndUpdateContent() {
    // Setting `src` attribute on `<include-fragment>` will trigger a load request,
    // which updates the list content.
    this.codespaceList?.setAttribute('src', this.codespaceList.getAttribute('data-src')!)
  }
}
